import React from 'react';
import FacilityStore from '../stores/FacilityStore';
import ViewerStore from '../stores/ViewerStore';
import ViewerActions from '../actions/ViewerActions';
import FacilityActions from '../actions/FacilityActions';
import OntologyActions from '../actions/OntologyActions';
import AssetActions from '../actions/AssetActions';
import ContextMenu from '../viewers/components/ContextMenu';
import ReactDOM from 'react-dom';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { compareNames } from '../helpers/nameComparison';
import { navigateAssetTag } from '../helpers/assetTagNavigationMenu';
import {
    formatIndexedMenuLabel,
    groupAssetTagsByScan,
} from '../helpers/assetIdentityMenuModel';

declare var InovxLoader: any;

const { useMemo, useRef, useEffect, useCallback, useState } = React;

const translateAssetListViewer = (key: string) => InovxLoader.t(`facility:viewer.${key}`);
const translateAssetListAction = (key: string) => InovxLoader.t(`facility:action.${key}`);

interface IAssetTagBarItem {
    ID: string;
    DisplayName: string;
    AssetID?: string;
    IconClass: string;
    Type: 'root' | 'imagetag' | '3dtag' | 'doc' | '3d';
    IsOccluded: boolean;
    IsInCurrentView?: boolean;
    ShowIcon?: boolean;
    Url?: string;
}

interface IAssetTagBarRootItem extends IAssetTagBarItem {
    Items: IAssetTagBarItem[];
    /** All identity children used for context-menu navigation, independent of the visible query filter. */
    MenuItems?: IAssetTagBarItem[];
    ItemType: 'assetIdentity' | 'hierarchyItem';
    HierarchyItemID?: string;
    Has3D?: boolean;
    Has360?: boolean;
    HasPID?: boolean;
}

type AssetListChildTypeFilter = 'all' | 'assetTagsOnly' | 'hierarchyItemsOnly';
export type AssetListMenuContext = 'facility-map' | 'pano-viewer' | 'three-d-viewer' | 'smart-asset-set' | 'fixed-asset-set';

export interface AssetListMenuItemDefinition {
    id: string;
    label: string;
    iconClass?: string;
    isDisabled?: boolean;
    execute: () => void;
}

export interface AssetListMenuItemContext {
    id: string;
    itemType: 'assetIdentity' | 'hierarchyItem';
    assetId?: string;
    hierarchyItemId?: string;
    displayName: string;
}

interface AssetListProps {
    mode: 'facility' | 'panoviewer' | '3dviewer';
    facilityAssets: IFacilityAssetsData;
    selectedOntologyId: string;
    selectedID?: string;
    viewId?: string;
    occludedTags?: string[];
    filteredAssets?: string[];
    assetFilter?: IAssetFilter;
    gridHeight?: number;
    onSelectedIDChange?: (selectedId: string | null) => void;
    childTypeFilter?: AssetListChildTypeFilter;
    hierarchyItems?: IAssetSetItem[];
    filterBySelectedOntology?: boolean;
    menuContext?: AssetListMenuContext;
    getAdditionalMenuItems?: (item: AssetListMenuItemContext) => AssetListMenuItemDefinition[];
}

interface IRowData {
    items: IAssetTagBarRootItem[];
    selectedRootID: string | null;
    showIndicators: boolean;
    onRowClick: (item: IAssetTagBarRootItem) => void;
    onOverflowClick: (event: React.MouseEvent<HTMLButtonElement>, item: IAssetTagBarRootItem) => void;
}

interface IContextMenuItem {
    id: string;
    label: string;
    iconClass?: string;
    isDisabled?: boolean;
    hasSeparatorBefore?: boolean;
    execute: () => void;
}

interface IContextMenuState {
    left: number;
    top: number;
    items: IContextMenuItem[];
}

interface IStopPropagationEvent {
    stopPropagation: () => void;
}

const assetClassIcons = ['Equipment', 'GenericAsset', 'InspectionPoint', 'Instrument', 'IsolationPoint', 'Pipeline'];
const EMPTY_STRING_ARRAY: string[] = [];
const ROW_HEIGHT = 36;
const HIERARCHY_ITEM_ROW_PREFIX = 'hierarchy:';

const getIconName = (className: string) => {
    if (className) {
        const classNameWithoutSpaces = className.replace(/\s/g, '');
        if (assetClassIcons.includes(classNameWithoutSpaces)) {
            return classNameWithoutSpaces;
        }

        return 'GenericAsset';
    }

    return 'AssetTag';
};

const AssetListRow = React.memo((props: ListChildComponentProps<IRowData>) => {
    const { index, style, data } = props;
    const item = data.items[index];
    const isSelected = data.selectedRootID === item.ID;

    const onRowClick = () => {
        data.onRowClick(item);
    };

    const indicators: Array<{ key: string; label: string; className: string }> = [];
    if (item.Has3D) indicators.push({ key: '3d', label: translateAssetListViewer('indicator3D'), className: 'asset-list-indicator-3d' });
    if (item.Has360) indicators.push({ key: '360', label: translateAssetListViewer('indicator360'), className: 'asset-list-indicator-360' });
    if (item.HasPID) indicators.push({ key: 'pid', label: translateAssetListViewer('indicatorPID'), className: 'asset-list-indicator-pid' });

    return (
        <div
            style={style}
            className={`asset-list-row tag-field cursor-pointer ${isSelected ? 'selected-row' : ''}`}
            onClick={onRowClick}
            title={item.DisplayName}
            data-asset-id={item.ID}
        >
            <div className='asset-list-left'>
                <span className={`tag-icon ${item.IconClass}`}></span>
                <span className='asset-list-name'>{item.DisplayName}</span>
            </div>
            <div className='asset-list-right'>
                {data.showIndicators && indicators.length > 0 && (
                    <div className='asset-list-indicators' title={indicators.map(i => i.label).join(', ')}>
                        {indicators.map((indicator) => (
                            <span key={indicator.key} className={indicator.className}>{indicator.label}</span>
                        ))}
                    </div>
                )}

                <button
                    type='button'
                    className='asset-list-overflow e-lib e-btn e-control e-flat e-icon-btn'
                    title={translateAssetListViewer('moreActions')}
                    onClick={(e) => {
                        e.stopPropagation();
                        data.onOverflowClick(e, item);
                    }}
                >
                    <span className='asset-list-overflow-icon'>&hellip;</span>
                </button>
            </div>
        </div>
    );
});

const AssetList = (props: AssetListProps) => {
    const listRef = useRef<FixedSizeList>(null);
    const [contextMenu, setContextMenu] = useState<IContextMenuState | null>(null);
    const occludedTags = props.occludedTags ?? EMPTY_STRING_ARRAY;
    const filteredAssets = props.filteredAssets ?? EMPTY_STRING_ARRAY;
    const hierarchyItems = props.hierarchyItems;
    const childTypeFilter: AssetListChildTypeFilter = props.childTypeFilter ?? 'all';
    const filterBySelectedOntology = props.filterBySelectedOntology ?? true;
    const menuContext: AssetListMenuContext = props.menuContext ?? (
        props.mode === 'facility' ? 'facility-map' : props.mode === 'panoviewer' ? 'pano-viewer' : 'three-d-viewer'
    );

    const filterChildItemsByType = useCallback((items: IAssetTagBarItem[]) => {
        if (childTypeFilter === 'assetTagsOnly') {
            return items.filter(i => i.Type === 'imagetag' || i.Type === '3dtag');
        }
        if (childTypeFilter === 'hierarchyItemsOnly') {
            return items.filter(i => i.Type === '3d');
        }
        return items;
    }, [childTypeFilter]);

    const rootItems = useMemo(() => {
        if (!props.facilityAssets) {
            return [] as IAssetTagBarRootItem[];
        }

        const selectedAssetTags = filterBySelectedOntology
            ? AssetActions.filterTagsBySelectedOntology(props.facilityAssets.AssetTags, props.selectedOntologyId)
            : props.facilityAssets.AssetTags;

        const tagsByAssetId = new Map<string, IAssetTag[]>();
        selectedAssetTags.forEach(tag => {
            const list = tagsByAssetId.get(tag.AssetID);
            if (list) list.push(tag);
            else tagsByAssetId.set(tag.AssetID, [tag]);
        });

        const nativesByAssetId = new Map<string, INativeAsset[]>();
        props.facilityAssets.NativeAssets.forEach(na => {
            const list = nativesByAssetId.get(na.AssetID);
            if (list) list.push(na);
            else nativesByAssetId.set(na.AssetID, [na]);
        });

        const occludedSet = new Set(occludedTags);
        const filteredSet = new Set(filteredAssets);

        const matchesSelectedOntology = filterBySelectedOntology
            ? (nativeAsset: INativeAsset) => {
                const applicableOntologyIDs = nativeAsset.ApplicableOntologyIDs;
                return !!applicableOntologyIDs && applicableOntologyIDs.indexOf(props.selectedOntologyId) >= 0;
            }
            : () => true;

        const getItemList = (nativeAssets: INativeAsset[], tags: IAssetTag[], currentViewId: string) => {
            tags.sort((a, b) => {
                if (a.View360ID === currentViewId && b.View360ID !== currentViewId) return -1;
                if (a.View360ID !== currentViewId && b.View360ID === currentViewId) return 1;
                return a.View?.Name != null ? compareNames(a.View.Name, b.View?.Name || '') : 0;
            });

            const panoTags = tags.filter(t => !!t.View360ID);
            const threeDTags = tags.filter(t => !t.View360ID);

            const tagItems: IAssetTagBarItem[] = [];

            panoTags.forEach((tag, index) => {
                const showIndex = panoTags.length > 1;
                const baseName = translateAssetListViewer('asset360ImageTag');
                const displayName = showIndex ? `${baseName} (${index + 1})` : baseName;

                tagItems.push({
                    ID: tag.ID,
                    AssetID: tag.AssetID,
                    DisplayName: displayName,
                    IconClass: 'Asset360ImageTag',
                    Type: 'imagetag',
                    IsOccluded: occludedSet.has(tag.ID),
                    IsInCurrentView: props.mode === '3dviewer' || !tag.View360ID || tag.View360ID === currentViewId,
                    ShowIcon: !!tag.View360ID || props.mode !== 'facility',
                });
            });

            threeDTags.forEach((tag, index) => {
                const showIndex = threeDTags.length > 1;
                const baseName = translateAssetListViewer('asset3DTag');
                const displayName = showIndex ? `${baseName} (${index + 1})` : baseName;

                tagItems.push({
                    ID: tag.ID,
                    AssetID: tag.AssetID,
                    DisplayName: displayName,
                    IconClass: 'Asset3DTag',
                    Type: '3dtag',
                    IsOccluded: occludedSet.has(tag.ID),
                    IsInCurrentView: props.mode === '3dviewer',
                    ShowIcon: props.mode !== 'facility',
                });
            });

            const vnfAssets = nativeAssets.filter(n => n.Source === AssetSourceType.Vnf && matchesSelectedOntology(n));
            const docxAssets = nativeAssets.filter(n => n.Source === AssetSourceType.DocX);
            const nativeAssetItems: IAssetTagBarItem[] = [];

            vnfAssets.forEach((nativeAsset, index) => {
                const showIndex = vnfAssets.length > 1;
                const baseName = translateAssetListViewer('asset3DAsset');
                const displayName = showIndex ? `${baseName} (${index + 1})` : baseName;

                nativeAssetItems.push({
                    ID: nativeAsset.HierarchyItemID,
                    AssetID: nativeAsset.AssetID,
                    DisplayName: displayName,
                    IconClass: 'Asset3DAsset',
                    Type: '3d',
                    IsOccluded: false,
                    IsInCurrentView: props.mode === '3dviewer',
                    ShowIcon: true,
                });
            });

            docxAssets.forEach((nativeAsset, index) => {
                const showIndex = docxAssets.length > 1;
                const baseName = translateAssetListViewer('asset2DPIDAsset');
                const displayName = showIndex ? `${baseName} (${index + 1})` : baseName;

                const fullDocxUrl = nativeAsset.DocumentUrl?.replace('docOnly=1', 'docOnly=0');
                nativeAssetItems.push({
                    ID: nativeAsset.ID,
                    AssetID: nativeAsset.AssetID,
                    DisplayName: displayName,
                    IconClass: 'Document',
                    Type: 'doc',
                    IsOccluded: false,
                    IsInCurrentView: true,
                    ShowIcon: true,
                    Url: fullDocxUrl,
                });
            });

            return [...tagItems, ...nativeAssetItems];
        };

        const assets: IAssetTagBarRootItem[] = props.facilityAssets.Assets.reduce((acc, asset) => {
            if (!filteredSet.size || !filteredSet.has(asset.ID)) {
                const tags = tagsByAssetId.get(asset.ID) || [];
                const nativeAssets = nativesByAssetId.get(asset.ID) || [];
                const allChildItems = getItemList(nativeAssets, tags, props.viewId);
                const childItems = filterChildItemsByType(allChildItems);

                if (childItems.length === 0) {
                    return acc;
                }

                acc.push({
                    ID: asset.ID,
                    AssetID: asset.ID,
                    ItemType: 'assetIdentity',
                    IconClass: getIconName(asset.AssetClass?.Name),
                    DisplayName: asset.Identity,
                    Items: childItems,
                    MenuItems: childItems.length === allChildItems.length ? undefined : allChildItems,
                    Has3D: childItems.some(i => i.Type === '3d' || i.Type === '3dtag'),
                    Has360: childItems.some(i => i.Type === 'imagetag'),
                    HasPID: childItems.some(i => i.Type === 'doc'),
                    Type: 'root',
                    IsOccluded: childItems[0]?.IsOccluded ?? false,
                    IsInCurrentView: childItems[0]?.IsInCurrentView ?? false,
                    ShowIcon: childItems[0]?.ShowIcon ?? false,
                });
            }
            return acc;
        }, [] as IAssetTagBarRootItem[]);

        if (hierarchyItems?.length && childTypeFilter !== 'assetTagsOnly') {
            hierarchyItems.forEach(item => {
                assets.push({
                    ID: `${HIERARCHY_ITEM_ROW_PREFIX}${item.ID}`,
                    ItemType: 'hierarchyItem',
                    HierarchyItemID: item.ID,
                    IconClass: 'GenericAsset',
                    DisplayName: item.Name,
                    Items: [],
                    Has3D: true,
                    Type: 'root',
                    IsOccluded: false,
                    IsInCurrentView: props.mode === '3dviewer',
                    ShowIcon: true,
                });
            });
        }

        assets.sort((a, b) => compareNames(a.DisplayName, b.DisplayName));
        return assets;
    }, [props.facilityAssets, filteredAssets, hierarchyItems, occludedTags, props.viewId, props.mode, props.selectedOntologyId, filterBySelectedOntology, childTypeFilter, filterChildItemsByType]);

    const rootIdSet = useMemo(() => {
        const ids = new Set<string>();
        rootItems.forEach(root => ids.add(root.ID));
        return ids;
    }, [rootItems]);

    const rootIndexById = useMemo(() => {
        const map = new Map<string, number>();
        rootItems.forEach((root, index) => map.set(root.ID, index));
        return map;
    }, [rootItems]);

    const childParentMap = useMemo(() => {
        const map = new Map<string, string>();
        rootItems.forEach(root => {
            root.Items?.forEach(child => {
                map.set(child.ID, root.ID);
            });
        });
        return map;
    }, [rootItems]);

    const hierarchyRootMap = useMemo(() => {
        const map = new Map<string, string>();
        rootItems.forEach(root => {
            if (root.ItemType === 'hierarchyItem' && root.HierarchyItemID) {
                map.set(root.HierarchyItemID, root.ID);
            }
        });
        return map;
    }, [rootItems]);

    const tagByID = useMemo(() => {
        const map = new Map<string, IAssetTag>();
        (props.facilityAssets?.AssetTags || []).forEach(tag => map.set(tag.ID, tag));
        return map;
    }, [props.facilityAssets]);

    const selectedRootID = useMemo(() => {
        if (!props.selectedID) return null;
        if (rootIdSet.has(props.selectedID)) return props.selectedID;
        return hierarchyRootMap.get(props.selectedID) || childParentMap.get(props.selectedID) || null;
    }, [props.selectedID, rootIdSet, hierarchyRootMap, childParentMap]);

    const hasUnsavedData = (continuation?: () => void | Promise<unknown>): boolean => {
        return ViewerActions.checkForKnowledgeViewChanges(continuation);
    };

    const openRangeView = async (focusType: 'asset' | 'tag', rangePosition: IVector3D) => {
        const ontology = FacilityStore.getRootOntologyPart();
        const measurements = ViewerStore.getMeasurements();

        OntologyActions.selectOntology(ontology);
        await FacilityActions.openLODViewer(ontology.ID, ['Point Cloud Data', 'CAD Data', 'Mesh Data'], 30.0, rangePosition, 0.001, false);

        const current = ViewerStore.getContentLayers();

        if (focusType === 'tag') {
            ViewerActions.setContentLayers({ ...current, assetTags: true });
            ViewerActions.updateMeasurements(measurements);
            return;
        }

        const next = { ...current };

        next.pointCloud = false;
        next.assets = true;
        next.assetTags = false;
        next.cad = false;
        next.positions360 = false;
        next.scanPositions = false;
        next.assemblies = false;
        next.mesh = false;

        ViewerActions.setContentLayers(next);
        ViewerActions.updateMeasurements(measurements);
    };

    const navigateImageTagToView = async (e: IStopPropagationEvent, tagID: string) => {
        const tag = props.facilityAssets?.AssetTags.find(t => t.ID === tagID);
        if (tag) {
            const view: IView360Detail = props.facilityAssets.Facility?.Views?.find(x => x.ID === tag.View360ID);
            const selectedView = ViewerStore.getSelectedViewImage();
            if (view && selectedView?.ID !== view.ID) {
                if (hasUnsavedData(() => navigateImageTagToView(e, tagID))) {
                    e.stopPropagation();
                    return;
                }

                ViewerActions.showPanoViewerImage(view, tag);
                ViewerActions.selectAnnotation(tag.ID);
            } else {
                ViewerActions.selectAnnotation(tag.ID);
                ViewerActions.focusAnnotation(Object.assign({}, tag));
            }
        }

        e.stopPropagation();
    };

    const navigate3DTagToView = async (e: IStopPropagationEvent, tagID: string) => {
        const tag = props.facilityAssets?.AssetTags.find(t => t.ID === tagID);
        if (tag) {
            if (props.mode === 'facility' || props.mode === 'panoviewer') {
                if (hasUnsavedData(() => navigate3DTagToView(e, tagID))) {
                    e.stopPropagation();
                    return;
                }

                await openRangeView('tag', tag.Target3D);
            } else if (ViewerStore.getRangePosition()) {
                ViewerActions.setRangePosition(tag.Target3D);
            }

            ViewerActions.selectAnnotation(tag.ID);
            ViewerActions.focusAnnotation(Object.assign({}, tag));
        }

        e.stopPropagation();
    };

    const navigate3DAssetToView = async (e: IStopPropagationEvent, hierarchyItemID: string) => {
        e.stopPropagation();

        const extent = await ViewerActions.getHierarchyItemExtents(hierarchyItemID);

        const min = {
            X: extent.MinExtents.X / 1000,
            Z: extent.MinExtents.Y / 1000,
            Y: extent.MinExtents.Z / 1000,
        };

        const max = {
            X: extent.MaxExtents.X / 1000,
            Z: extent.MaxExtents.Y / 1000,
            Y: extent.MaxExtents.Z / 1000,
        };

        const mid = {
            X: min.X + (max.X - min.X) / 2,
            Y: min.Y + (max.Y - min.Y) / 2,
            Z: min.Z + (max.Z - min.Z) / 2,
        };

        const selectedAssetSet = ViewerStore.getSelectedAssetSet();
        const shouldOpen3DViewer = props.mode === 'facility' || props.mode === 'panoviewer' ||
            (props.mode === '3dviewer' && !ViewerStore.is3DViewerVisible());
        if (shouldOpen3DViewer) {
            if (hasUnsavedData(() => navigate3DAssetToView(e, hierarchyItemID))) return;
            await openRangeView('asset', mid);
            // Opening the 3D viewer resets viewer content, including the
            // selected Asset Set that controls the Manage Asset Sets results.
            if (selectedAssetSet) await ViewerActions.selectAssetSet(selectedAssetSet);
        } else if (ViewerStore.getRangePosition()) {
            ViewerActions.setRangePosition(mid);
        }

        ViewerActions.selectHierarchyItem(hierarchyItemID);
        ViewerActions.focusHierarchyItem(hierarchyItemID);
    };

    const viewDocument = (e: IStopPropagationEvent, itemUrl: string) => {
        if (itemUrl) {
            window.open(itemUrl, '_blank');
        }
        e.stopPropagation();
    };

    const buildMenuItems = useCallback((rootItem: IAssetTagBarRootItem): IContextMenuItem[] => {
        const noopEvent = {
            stopPropagation: () => { }
        };

        const menuChildren = rootItem.MenuItems ?? rootItem.Items;
        const threeDAssetChildren = menuChildren.filter(i => i.Type === '3d');
        const threeDTagChildren = menuChildren.filter(i => i.Type === '3dtag');
        const imageChildren = menuChildren.filter(i => i.Type === 'imagetag');
        const pidChildren = menuChildren.filter(i => i.Type === 'doc');

        const menuItems: IContextMenuItem[] = [];
        const appendGroup = (groupItems: IContextMenuItem[]) => {
            if (groupItems.length === 0) return;
            groupItems[0].hasSeparatorBefore = menuItems.length > 0;
            menuItems.push(...groupItems);
        };

        const appendAdditionalMenuItems = () => {
            const additionalMenuItems = props.getAdditionalMenuItems?.({
                id: rootItem.ID,
                itemType: rootItem.ItemType,
                assetId: rootItem.AssetID,
                hierarchyItemId: rootItem.HierarchyItemID,
                displayName: rootItem.DisplayName,
            }) ?? [];

            appendGroup(additionalMenuItems.map(item => ({
                    id: item.id,
                    label: item.label,
                    iconClass: item.iconClass,
                    execute: item.execute,
                })));
        };

        if (menuContext === 'fixed-asset-set') {
            appendAdditionalMenuItems();
        }

        const hierarchyAssetItems: IContextMenuItem[] = rootItem.ItemType === 'hierarchyItem' && rootItem.HierarchyItemID
            ? [{
                id: `3d-${rootItem.HierarchyItemID}`,
                label: translateAssetListAction('flyTo3DAsset'),
                iconClass: 'new-view-focus-icon',
                execute: () => navigate3DAssetToView(noopEvent, rootItem.HierarchyItemID),
            }]
            : [];

        const rangeViewAssetLimit = menuContext === 'facility-map' || menuContext === 'pano-viewer' ? 2 : 5;
        const rangeViewAssetChildren = threeDAssetChildren.slice(0, rangeViewAssetLimit);
        const threeDAssetLabel = menuContext === 'facility-map' || menuContext === 'pano-viewer'
            ? translateAssetListAction('flyTo3DAssetInRangeView')
            : translateAssetListAction('flyTo3DAsset');

        const append3DAssetItems = () => {
            appendGroup([
                ...hierarchyAssetItems,
                ...rangeViewAssetChildren.map((child, index) => ({
                    id: `3d-${child.ID}-${index}`,
                    label: formatIndexedMenuLabel(
                        threeDAssetLabel,
                        index,
                        rangeViewAssetChildren.length,
                        threeDAssetChildren.length,
                        translateAssetListAction('menuOverflowSuffix'),
                    ),
                    iconClass: 'new-view-focus-icon',
                    execute: () => navigate3DAssetToView(noopEvent, child.ID),
                })),
            ]);
        };

        if (menuContext !== 'pano-viewer') {
            append3DAssetItems();
        }

        const panoTags = imageChildren
            .map(child => tagByID.get(child.ID))
            .filter((tag): tag is IAssetTag => !!tag);
        const panoTagGroups = groupAssetTagsByScan(panoTags.map(tag => ({
            ...tag,
            ViewName: tag.View?.Name ?? props.facilityAssets.Facility?.Views?.find(view => view.ID === tag.View360ID)?.Name ?? '',
        })));

        if (menuContext === 'pano-viewer') {
            const panoTagItems: IContextMenuItem[] = [];
            const currentTags = panoTags.filter(tag => tag.View360ID === props.viewId);
            panoTagItems.push(...currentTags.map((tag, index) => ({
                    id: `focus-pano-tag-${tag.ID}`,
                    label: formatIndexedMenuLabel(
                        translateAssetListAction('focusTag'),
                        index,
                        currentTags.length,
                        currentTags.length,
                        translateAssetListAction('menuOverflowSuffix'),
                    ),
                    iconClass: 'view-focus-icon',
                    execute: () => { void navigateAssetTag('openTagScan', tag); },
                })));

            panoTagItems.push(...panoTagGroups
                .filter(group => group.scanID !== props.viewId)
                .map(group => ({
                        id: `open-scan-${group.scanID}`,
                        label: `${translateAssetListAction('openScanPosition')} '${group.scanName}'`,
                        iconClass: 'view-focus-icon',
                        execute: () => { void navigateAssetTag('openTagScan', group.tag); },
                    })));
            appendGroup(panoTagItems);
        } else {
            appendGroup(panoTagGroups.map(group => ({
                    id: `open-scan-${group.scanID}`,
                    label: `${translateAssetListAction('openScanPosition')} '${group.scanName}'`,
                    iconClass: 'view-focus-icon',
                    execute: () => { void navigateAssetTag('openTagScan', group.tag); },
                })));
        }

        if (menuContext === 'pano-viewer') {
            append3DAssetItems();
        }

        const threeDTagLimit = menuContext === 'facility-map' || menuContext === 'pano-viewer' ? 1 : 5;
        const limitedThreeDTagChildren = threeDTagChildren.slice(0, threeDTagLimit);
        const threeDTagLabelTotal = menuContext === 'facility-map' || menuContext === 'pano-viewer'
            ? limitedThreeDTagChildren.length
            : threeDTagChildren.length;
        const threeDTagLabel = menuContext === 'facility-map' || menuContext === 'pano-viewer'
            ? translateAssetListAction('flyToTagInRangeView')
            : translateAssetListAction('flyTo3DTag');
        appendGroup(limitedThreeDTagChildren.map((child, index) => ({
                id: `3dtag-${child.ID}-${index}`,
                label: formatIndexedMenuLabel(
                    threeDTagLabel,
                    index,
                    limitedThreeDTagChildren.length,
                    threeDTagLabelTotal,
                    translateAssetListAction('menuOverflowSuffix'),
                ),
                iconClass: 'new-view-focus-icon',
                execute: () => navigate3DTagToView(noopEvent, child.ID),
            })));

        appendGroup(pidChildren.map((child, index) => ({
                id: `pid-${child.ID}-${index}`,
                label: formatIndexedMenuLabel(
                    translateAssetListAction('openPIDDocument'),
                    index,
                    pidChildren.length,
                    pidChildren.length,
                    translateAssetListAction('menuOverflowSuffix'),
                ),
                iconClass: 'view-document-icon',
                execute: () => viewDocument(noopEvent, child.Url),
            })));

        if (menuContext !== 'fixed-asset-set') {
            appendAdditionalMenuItems();
        }

        return menuItems;
    }, [props.getAdditionalMenuItems, props.mode, props.facilityAssets, props.viewId, childTypeFilter, tagByID, menuContext]);

    const onOverflowClick = useCallback((event: React.MouseEvent<HTMLButtonElement>, item: IAssetTagBarRootItem) => {
        const items = buildMenuItems(item);
        if (!items.length) {
            setContextMenu(null);
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const left = rect.right + 4;
        const top = rect.top - 2;

        setContextMenu({
            left,
            top,
            items,
        });
    }, [buildMenuItems]);

    const onContextMenuItemClick = useCallback((itemId: string) => {
        if (!contextMenu) {
            return;
        }

        const selected = contextMenu.items.find(item => item.id === itemId);
        if (selected) {
            selected.execute();
        }

        setContextMenu(null);
    }, [contextMenu]);

    const selectItem = useCallback((item: IAssetTagBarRootItem | null) => {
        let selectedID: string = null;

        const layers = ViewerStore.getContentLayers();
        const is3DLayerOn = layers.assets;
        const isTagLayerOn = layers.assetTags;

        if (!item) {
            ViewerActions.selectAnnotation(null);
            props.onSelectedIDChange?.(null);
            return;
        }

        if (item.ItemType === 'hierarchyItem' && item.HierarchyItemID) {
            void ViewerActions.selectHierarchyItem(item.HierarchyItemID);
            props.onSelectedIDChange?.(item.HierarchyItemID);
            return;
        }

        // A 3D asset row resolves to a hierarchy item in the viewer. Select it
        // directly so the property splitter does not see an intermediate asset
        // selection before the hierarchy-item request completes.
        if (props.mode === '3dviewer' && is3DLayerOn) {
            const hierarchyItem = item.Items.find(child => child.Type === '3d');
            if (hierarchyItem) {
                void ViewerActions.selectHierarchyItem(hierarchyItem.ID);
                props.onSelectedIDChange?.(hierarchyItem.ID);
                return;
            }
        }

        ViewerActions.selectAsset(item.AssetID);

        if (props.mode === 'facility') {
            props.onSelectedIDChange?.(null);
            return;
        }

        if (props.mode === '3dviewer') {
            let child: IAssetTagBarItem | undefined;

            if (is3DLayerOn) child = item.Items.find(i => i.Type === '3d');
            if (!child && isTagLayerOn) child = item.Items.find(i => i.Type === '3dtag');

            if (child) {
                if (child.Type === '3d' && is3DLayerOn) {
                    selectedID = child.ID;
                    ViewerActions.selectHierarchyItem(selectedID);
                } else if (child.Type === 'imagetag' || child.Type === '3dtag') {
                    const tag = tagByID.get(child.ID);
                    if (tag) {
                        selectedID = tag.ID;
                    }
                }
            }
        } else if (props.mode === 'panoviewer') {
            const pano = item.Items.find(i => i.Type === 'imagetag' && i.IsInCurrentView);

            if (pano) {
                const tag = tagByID.get(pano.ID);
                if (tag) {
                    selectedID = tag.ID;
                }
            }
        }

        if (selectedID) {
            ViewerActions.selectAnnotation(selectedID);
            props.onSelectedIDChange?.(selectedID || null);
        }
    }, [props.mode, props.onSelectedIDChange, tagByID]);

    const onBackgroundClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as Element;
        const clickedRow = target.closest('.asset-list-row');
        if (!clickedRow) {
            selectItem(null);
        }
    }, [selectItem]);

    useEffect(() => {
        if (!selectedRootID) {
            return;
        }

        const index = rootIndexById.get(selectedRootID);
        if (index !== undefined && listRef.current) {
            listRef.current.scrollToItem(index, 'smart');
        }
    }, [selectedRootID, rootIndexById]);

    const listHeight = (typeof props.gridHeight === 'number' && props.gridHeight > 0) ? props.gridHeight : 300;

    const rowData = useMemo(() => ({
        items: rootItems,
        selectedRootID,
        showIndicators: childTypeFilter === 'all',
        onRowClick: selectItem,
        onOverflowClick,
    }), [rootItems, selectedRootID, childTypeFilter, selectItem, onOverflowClick]);

    return (
        <div className='control-section asset-tag-grid asset-list-host'>
            <div className='asset-list-container' onClick={onBackgroundClick}>
                {rootItems.length === 0 ? (
                    <div className='asset-list-empty'>{InovxLoader.t('facility:message.noResults')}</div>
                ) : (
                    <FixedSizeList
                        ref={listRef}
                        height={listHeight}
                        width='100%'
                        itemCount={rootItems.length}
                        itemSize={ROW_HEIGHT}
                        itemData={rowData}
                        itemKey={(index, data) => data.items[index].ID}
                    >
                        {AssetListRow}
                    </FixedSizeList>
                )}
            </div>
            {contextMenu && ReactDOM.createPortal(
                <ContextMenu
                    left={contextMenu.left}
                    top={contextMenu.top}
                    items={contextMenu.items.map(item => ({ id: item.id, label: item.label, iconClass: item.iconClass, isDisabled: item.isDisabled, hasSeparatorBefore: item.hasSeparatorBefore }))}
                    onItemClick={onContextMenuItemClick}
                    onRequestClose={() => setContextMenu(null)}
                    containerId='asset-list-context-menu'
                    zIndex={2000}
                />,
                document.body
            )}
        </div>
    );
};

export default AssetList;
